import type { Dict } from '../../i18n';
import type { Capability } from '@jalupro/shared';

/**
 * Human name + one-line description per capability (JP-CRM Area 1 §4.3 rows). Shown in
 * the permission picker; the technical key is shown underneath for reference.
 */
export const CAPABILITY_LABELS: Record<Capability, { en: string; desc: string }> = {
  'dashboard.financial': { en: 'Dashboard — financial tiles', desc: 'Revenue and average-order tiles in euros on the admin dashboard.' },
  'dashboard.financePage': { en: 'Financial page', desc: 'Commission paid, open items, breakdown by payment method and province.' },
  'dashboard.volume': { en: 'Dashboard — volume & activity tiles', desc: 'Orders, units, shipments, new clients and the activity feed on the admin dashboard.' },
  'dashboard.warehouse': { en: 'Dashboard — warehouse worklist', desc: 'To pick / packed / handed over today; the warehouse queue.' },
  'orders.list': { en: 'Orders — list, all', desc: 'See the order list across every agent (an Agent: own orders only).' },
  'orders.create': { en: 'Orders — create', desc: 'Enter a new order through the wizard (on an agent’s behalf for staff).' },
  'orders.editBeforeProforma': { en: 'Orders — amend until Packed', desc: 'Change lines, quantities or delivery address while the warehouse has not packed; the proforma is re-issued.' },
  'orders.manualStatus': { en: 'Orders — manual status change', desc: 'Force a fulfilment state by hand, with a mandatory reason (never the payment state).' },
  'orders.cancelArchive': { en: 'Orders — cancel', desc: 'Cancel an order with a reason: Agent and Sales Manager until Packed, Business Manager after Packed, before the hand-over scan.' },
  'orders.value': { en: 'Order value and totals', desc: 'See net, VAT and total of orders in euros. This row decides who sees money at all.' },
  'orders.releaseCommercial': { en: 'Orders — release hold, commercial grounds', desc: 'Release an order held for a first order, a discount above limit, Trainer cap or an unvalidated tax number.' },
  'orders.releaseFinancial': { en: 'Orders — release hold, financial grounds', desc: 'Release an order held for amount above threshold, restricted product, overdue item, payment risk or a blocked lot.' },
  'clients.view': { en: 'Clients — view', desc: 'Open client records and the client list (an Agent: own clients only).' },
  'clients.write': { en: 'Clients — create and edit', desc: 'Create clients, edit addresses, contacts, tax number, language; link clinic entities.' },
  'clients.reassign': { en: 'Clients — reassign to another agent', desc: 'Move a client to another owning agent, with a reason; history travels with the record.' },
  'clients.deleteAnonymise': { en: 'Clients — delete or anonymise (RGPD)', desc: 'Delete a client, or erase their personal data under RGPD while keeping fiscal history.' },
  'clients.merge': { en: 'Clients — merge duplicates', desc: 'Merge two doctor profiles or two clinic entities into one; never destructive.' },
  'tags.manage': { en: 'Tags — create, rename, retire', desc: 'Maintain the tag vocabulary itself (names, themes, retirement).' },
  'tags.apply': { en: 'Tags — apply to / remove from a client', desc: 'Put existing tags on a client or take them off.' },
  'products.view': { en: 'Products — view', desc: 'See the product catalog (names, SKUs, units per box).' },
  'products.prices': { en: 'Product prices', desc: 'See the net price and VAT rate of products.' },
  'products.edit': { en: 'Products — edit or sync', desc: 'Change product data or trigger the Holded catalog sync.' },
  'stock.view': { en: 'Stock levels, lots, expiry', desc: 'See on-hand / reserved / available stock, lots and expiry dates.' },
  'stock.goodsIn': { en: 'Goods-in, lot registration', desc: 'Book incoming goods and register lots.' },
  'stock.shortfall': { en: 'Shortfall list', desc: 'See orders waiting for goods and decide partial deliveries.' },
  'stock.pick': { en: 'Pick list, pack, hand-over', desc: 'Work the dispatch queue: pick by lot, pack, confirm hand-over to the carrier.' },
  'payments.view': { en: 'Payments — view', desc: 'See payments, bank credits and the reconciliation queue.' },
  'payments.reconcile': { en: 'Payments — reconcile, confirm a match', desc: 'Match a bank credit to a proforma or confirm a proposed match.' },
  'mandates.view': { en: 'Mandates — view', desc: 'See SEPA mandates (Assistant: state only, no bank data).' },
  'mandates.activate': { en: 'Mandates — verify and activate', desc: 'Verify a received mandate and activate it for collections.' },
  'documents.view': { en: 'Invoices and fiscal documents', desc: 'See proformas, invoices, delivery notes; at F/R also re-send them to the client.' },
  'creditNotes.raise': { en: 'Credit notes — raise', desc: 'Issue a credit note in Holded for a return.' },
  'returns.record': { en: 'Returns — record, decide', desc: 'Record a return and decide replacement or credit (Agent: record only).' },
  'agents.list': { en: 'Agents — list and performance', desc: 'See the agent list with orders, units and activity (euros only with sales-performance rights).' },
  'agents.manage': { en: 'Agents — invite, block, assign office', desc: 'Create agent accounts, block them, set their office.' },
  'commission.own': { en: 'Commission — own', desc: 'See one’s own commission and statements.' },
  'commission.all': { en: 'Commission — all agents', desc: 'See every agent’s commission.' },
  'commission.settle': { en: 'Commission — rates, settlement run', desc: 'Set commission rates per agent and run the monthly settlement.' },
  'reports.moneyOps': { en: 'Reports — money operations', desc: 'Receivables, collections, returned debits, invoice register.' },
  'reports.salesEuros': { en: 'Reports — sales and performance in euros', desc: 'Revenue per agent, client, product and region in euros.' },
  'reports.volume': { en: 'Reports — volume, units, activity', desc: 'The same reports in units, orders and activity — no euros.' },
  'exports.withAmounts': { en: 'Exports — with amounts', desc: 'Download files that contain euro amounts.' },
  'exports.withoutAmounts': { en: 'Exports — without amounts', desc: 'Download files in units and status only.' },
  'calendar.view': { en: 'Calendar — view', desc: 'See the shared calendar (visits, workshops, congresses).' },
  'calendar.write': { en: 'Calendar — create and edit entries', desc: 'Add and change calendar entries.' },
  'users.manage': { en: 'Users — invite, change role, reset 2FA', desc: 'Administer every account: invite, change roles, reset two-factor. Super Admin only.' },
  'settings.manage': { en: 'Settings — integrations, offices, tax, thresholds', desc: 'System settings, offices, thresholds and this permission matrix. Super Admin only.' },
  'audit.view': { en: 'Audit log', desc: 'Read the audit log (an Agent: own orders and clients only).' },
};

const dict: Dict = {
  en: {
    heading: 'Permissions',
    intro: 'Pick a permission to see what it allows and which level each role holds. Levels: F full · W write within own scope · R read · U units & status only · O own records only · — no access. Financial figures are visible to the Super Admin only.',
    roles_heading: 'Roles in the system',
    pick_label: 'Permission',
    pick_placeholder: 'Choose a permission…',
    all_default: 'Every permission is at its document default.',
    override_count: '{{count}} setting(s) differ from the document default.',
    unsaved: '{{count}} unsaved change(s)',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Saved {{count}} change(s).',
    reset_all: 'Reset everything to default',
    reset_all_confirm: 'Remove every override and return all permissions to the document default?',
    reset_done: 'All permissions reset to the document default.',
    reset_row: 'Reset this permission to default',
    col_role: 'Role',
    col_level: 'Level',
    col_default: 'Default',
    locked_sa: 'Super Admin always has full access.',
    locked_reserved: 'Reserved to the Super Admin.',
    agent_rule: 'An Agent can hold a permission only for their own records (O), or not at all.',
    loading: 'Loading…',
    level_F: 'Full',
    level_W: 'Write within own scope',
    level_R: 'Read',
    level_U: 'Read — units & status only',
    level_O: 'Own records only',
    level_none: 'No access',
    group_dashboard: 'Dashboard',
    group_orders: 'Orders',
    group_clients: 'Clients',
    group_tags: 'Tags',
    group_products: 'Products',
    group_stock: 'Stock & warehouse',
    group_payments: 'Payments',
    group_mandates: 'Mandates',
    group_documents: 'Documents',
    group_creditNotes: 'Credit notes',
    group_returns: 'Returns',
    group_agents: 'Agents',
    group_commission: 'Commission',
    group_reports: 'Reports',
    group_exports: 'Exports',
    group_calendar: 'Calendar',
    group_users: 'Users',
    group_settings: 'Settings',
    group_audit: 'Audit',
  },
  es: {
    heading: 'Permisos',
    intro: 'Elige un permiso para ver qué permite y qué nivel tiene cada rol. Niveles: F completo · W escritura en su ámbito · R lectura · U solo unidades y estado · O solo registros propios · — sin acceso. Los importes solo los ve el Super Admin.',
    roles_heading: 'Roles del sistema',
    pick_label: 'Permiso',
    pick_placeholder: 'Elige un permiso…',
    all_default: 'Todos los permisos están en su valor por defecto.',
    override_count: '{{count}} ajuste(s) difieren del valor por defecto.',
    unsaved: '{{count}} cambio(s) sin guardar',
    save: 'Guardar cambios',
    saving: 'Guardando…',
    saved: 'Guardados {{count}} cambio(s).',
    reset_all: 'Restablecer todo',
    reset_all_confirm: '¿Eliminar todas las modificaciones y devolver todos los permisos al valor por defecto?',
    reset_done: 'Todos los permisos restablecidos.',
    reset_row: 'Restablecer este permiso',
    col_role: 'Rol',
    col_level: 'Nivel',
    col_default: 'Por defecto',
    locked_sa: 'El Super Admin siempre tiene acceso completo.',
    locked_reserved: 'Reservado al Super Admin.',
    agent_rule: 'Un agente solo puede tener un permiso sobre sus propios registros (O), o no tenerlo.',
    loading: 'Cargando…',
    level_F: 'Completo',
    level_W: 'Escritura en su ámbito',
    level_R: 'Lectura',
    level_U: 'Lectura — solo unidades y estado',
    level_O: 'Solo registros propios',
    level_none: 'Sin acceso',
    group_dashboard: 'Panel',
    group_orders: 'Pedidos',
    group_clients: 'Clientes',
    group_tags: 'Etiquetas',
    group_products: 'Productos',
    group_stock: 'Stock y almacén',
    group_payments: 'Cobros',
    group_mandates: 'Mandatos',
    group_documents: 'Documentos',
    group_creditNotes: 'Abonos',
    group_returns: 'Devoluciones',
    group_agents: 'Agentes',
    group_commission: 'Comisiones',
    group_reports: 'Informes',
    group_exports: 'Exportaciones',
    group_calendar: 'Agenda',
    group_users: 'Usuarios',
    group_settings: 'Ajustes',
    group_audit: 'Auditoría',
  },
  de: {
    heading: 'Berechtigungen',
    intro: 'Wählen Sie eine Berechtigung, um zu sehen, was sie erlaubt und welche Stufe jede Rolle hat. Stufen: F voll · W schreiben im eigenen Bereich · R lesen · U nur Stück & Status · O nur eigene Datensätze · — kein Zugriff. Beträge sieht nur der Super Admin.',
    roles_heading: 'Rollen im System',
    pick_label: 'Berechtigung',
    pick_placeholder: 'Berechtigung wählen…',
    all_default: 'Alle Berechtigungen auf dem Dokument-Standard.',
    override_count: '{{count}} Einstellung(en) weichen vom Standard ab.',
    unsaved: '{{count}} ungespeicherte Änderung(en)',
    save: 'Änderungen speichern',
    saving: 'Wird gespeichert…',
    saved: '{{count}} Änderung(en) gespeichert.',
    reset_all: 'Alles zurücksetzen',
    reset_all_confirm: 'Alle Änderungen entfernen und alle Berechtigungen auf den Standard zurücksetzen?',
    reset_done: 'Alle Berechtigungen zurückgesetzt.',
    reset_row: 'Diese Berechtigung zurücksetzen',
    col_role: 'Rolle',
    col_level: 'Stufe',
    col_default: 'Standard',
    locked_sa: 'Der Super Admin hat immer vollen Zugriff.',
    locked_reserved: 'Dem Super Admin vorbehalten.',
    agent_rule: 'Ein Agent kann eine Berechtigung nur für eigene Datensätze (O) halten oder gar nicht.',
    loading: 'Wird geladen…',
    level_F: 'Voll',
    level_W: 'Schreiben im eigenen Bereich',
    level_R: 'Lesen',
    level_U: 'Lesen — nur Stück & Status',
    level_O: 'Nur eigene Datensätze',
    level_none: 'Kein Zugriff',
    group_dashboard: 'Dashboard',
    group_orders: 'Bestellungen',
    group_clients: 'Kunden',
    group_tags: 'Tags',
    group_products: 'Produkte',
    group_stock: 'Bestand & Lager',
    group_payments: 'Zahlungen',
    group_mandates: 'Mandate',
    group_documents: 'Dokumente',
    group_creditNotes: 'Gutschriften',
    group_returns: 'Retouren',
    group_agents: 'Agenten',
    group_commission: 'Provision',
    group_reports: 'Berichte',
    group_exports: 'Exporte',
    group_calendar: 'Kalender',
    group_users: 'Benutzer',
    group_settings: 'Einstellungen',
    group_audit: 'Audit',
  },
};

export default dict;
